import React, { useCallback, useState } from 'react';
import { useForm } from "react-hook-form";
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { URL_PRISMA_HOME, URL_BACKOFFICE, BFF_ONBOARDING_URL } from '../../index';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Box, Button, Link } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import InputAdornment from '@material-ui/core/InputAdornment';
import { green } from '@material-ui/core/colors';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import publicIp from 'public-ip';
import { exactLength, onlyNumber } from "../Validations/HelperValidations";
import ValidationMessages from './ValidationMessages';
import { Establishment } from "../../models/Establishment";
import { decryptSymmetric } from "../../utils/DecryptionUtils";
import { ProductTypeUtils } from "../../utils/ProductTypeUtils";


interface CuitType {
    handleNext(cuit: string, establishments: any): void
    setEstablishments(establishments: Establishment[]): void
}

const colorBlue = "#023F86";
const colorLightBlue = "#30c2c2";
const ERROR_500_MESSAGE = "Se produjo un error recuperando datos.";
const CAPTCHA_ERROR_MESSAGE = "Se produjo un error validando el captcha.";

const useStyles = makeStyles({
    fontText: {
        color: colorBlue,
    },
    linkStyle: {
        color: colorBlue,
    },
    linkStyle2: {
        color: colorLightBlue,
    },
    redirectLink: {
        color: '#FFFFFF',
        textDecoration: 'none'
    },
    tickStyle: {
        color: green.A400,
        fontSize: 40
    },
    buttonContinueStyle: {
        backgroundColor: colorBlue,
        color: '#FFFFFF',
        width: 230,
        height: 70,
        transition: 'background-color 0.3s ease',
        '&:hover': {
            backgroundColor: lighten(colorBlue, 0.2)
        }
    },
    circularProgress: {
        marginLeft: '8px'
    },
    buttonAction: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '2rem',
    },
    buttonStyle: {
        backgroundColor: '#FFFFFF',
        color: colorBlue,
        border: '1px solid #023F86',
        width: 230,
        height: 70,
    },
    subtitle: {
        color: colorBlue,
        textAlign: 'left',
        marginLeft: 20,
    },
    image: {
        verticalAlign: "top",
        fontSize: 65,
        color: colorLightBlue
    },
    tableStyle: {
        backgroundColor: '#f5f5f5',
    },
    textStyle: {
        color: colorBlue,
        textAlign: 'left',
        marginLeft: 25,
        fontSize: '1.1rem'
    },
    blueText: {
        color: colorBlue
    }
});


const CuitForm: React.FC<CuitType> = ({ handleNext, setEstablishments }) => {

    const { executeRecaptcha } = useGoogleReCaptcha();
    const [dynamicAction, setDynamicAction] = useState('homepage');
    const [cuit, setCuit] = useState('');
    const [token, setToken] = useState('');
    const [ip, setIp] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [showRegisterLink, setShowRegisterLink] = useState(false);
    const [showSEMLink, setShowSEMLink] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showRegisterText, setShowRegisterText] = useState(false);
    const [showConfirmEmail, setShowConfirmEmail] = useState(false);
    const [alreadyRegisteredUser, setAlreadyRegisteredUser] = useState(false)
    const [helperText, setHelperText] = useState('')
    const [okCheck, setOkCheck] = useState(false)
    const [showConfirmEmailSent, setShowConfirmEmailSent] = useState(false)
    const { handleSubmit } = useForm();

    function cleanMsgs() {
        setErrorMsg('')
        setHelperText('')
    }

    const onSubmit = async () => {
        setLoading(true);
        try {
            const res = await fetch(BFF_ONBOARDING_URL + "/accounts/" + cuit, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json',
                    'Recaptcha-Token': token,
                    'X-Client-IP': ip,
                    'From-Host': window.location.host
                }
            })

            switch (res.status) {
                case 200: {
                    const encryptedData = await res.json();
                    const decryptedData = await decryptSymmetric(encryptedData.encryptedData, encryptedData.iv);
                    const data = JSON.parse(decryptedData);

                    if (data.products) {
                        const hasProductDPLus = data.products.some((product: { id: number }) => product.id === ProductTypeUtils.DPlus);
                        if (hasProductDPLus) {
                            setAlreadyRegisteredUser(true);
                        } else {
                            const establishments = await getEstablishmentsByCuit();
                            if (establishments.length > 0) {
                                handleNext(cuit, establishments);
                            }
                        }
                    } else {
                        const establishments = await getEstablishmentsByCuit();
                        if (establishments.length > 0) {
                            handleNext(cuit, establishments);
                        }
                    }
                    break;
                }
                case 404:
                    const establishments = await getEstablishmentsByCuit();
                    if (establishments.length > 0) {
                        handleNext(cuit, establishments);
                    }
                    break;
                case 401:
                    setErrorMsg(CAPTCHA_ERROR_MESSAGE);
                    break;
                case 500:
                    setErrorMsg(ERROR_500_MESSAGE);
                    break;
                default:
                    break;
            }
        } catch (error) {
            setErrorMsg(error.message);
        } finally {
            setLoading(false);
            resetToken();
        }
    };

    const getEstablishmentsByCuit = async (): Promise<Establishment[]> => {
        try {
            const res = await fetch(`${BFF_ONBOARDING_URL}/establishments?cuit=${cuit}`, {
                method: 'get',
                headers: { 'Content-Type': 'application/json' }
            })

            switch (res.status) {
                case 200: {
                    const encryptedData = await res.json();
                    const decryptedData = await decryptSymmetric(encryptedData.encryptedData, encryptedData.iv);
                    const data = JSON.parse(decryptedData);

                    if (Array.isArray(data) && data.length === 0) {
                        setShowRegisterLink(true);
                        setShowRegisterText(true);
                        setErrorMsg('Parece que tu comercio no está registrado en Payway. ¡Regístrate!');
                        return []
                    } else {
                        const retrievedEstablishments = data.map((est: any) => buildEstablishment(est));
                        if (!existsAllLeastOneTerminal(retrievedEstablishments)) {
                            setErrorMsg('Parece que tu establecimiento no tiene una terminal asociada. ¡Solícitala!');
                            return []
                        } else {
                            return retrievedEstablishments
                        }
                    }
                }
                case 500:
                    setErrorMsg(ERROR_500_MESSAGE);
                    return []
                default:
                    return []
            }
        } catch (error) {
            setErrorMsg('Ocurrió un error al obtener los establecimientos.');
            return []
        }
    };

    const existsAllLeastOneTerminal = (establishments: Establishment[]): boolean => {
        const establishmentsWithoutEmptyTerminals =
            establishments.filter(e =>
                (e.terminals !== null)
            ).filter(e => (e.terminals?.length || 0) > 0)
        if (establishmentsWithoutEmptyTerminals.length > 0) {
            setEstablishments(establishmentsWithoutEmptyTerminals)
        }
        return establishmentsWithoutEmptyTerminals.length > 0
    }

    const buildEstablishment = ({ code, terminals, brands, business_name, location, mcc }: any): Establishment => {
        const {
            address = "",
            city = "",
            zip_code = ""
        } = location || {};

        return {
            code: code,
            terminals: terminals,
            brands: brands,
            business_name: business_name,
            address: address,
            city: city,
            zip_code: zip_code,
            mcc: mcc
        }
    }


    const resetToken = useCallback(async () => {
        if (!executeRecaptcha) {
            return;
        }

        const result = await executeRecaptcha('dynamicAction');
        setToken(result);
    }, [executeRecaptcha]);

    const onChangeCuitNumber = (event: any) => {
        const numericValue = onlyNumber(event, 11)
        setOkCheck(false)
        setShowConfirmEmail(false)
        setShowConfirmEmailSent(false)
        if (exactLength(numericValue.length, 11)) {
            cleanMsgs()
            publicIp.v4().then(ipData => {
                setIp(ipData);
                setIsValid(true);
            })
        } else {
            setIsValid(false);
            setErrorMsg('El CUIT consta de 11 dígitos.');
        }
        setCuit(numericValue);
    }

    const resetForm = () => {
        setCuit('');
        cleanMsgs();
        setShowRegisterLink(false);
        setIsValid(false);
        setShowConfirmEmail(false);
        setOkCheck(false);
        setAlreadyRegisteredUser(false);
        setShowConfirmEmailSent(false);
        setShowSEMLink(false);
    }

    const goToPrismaRegister = () => {
        window.location.replace(URL_PRISMA_HOME);
        return;
    }

    const goToBackoffice = () => {
        window.location.replace(URL_BACKOFFICE);
        return;
    }

    const classes = useStyles();

    return (
        <React.Fragment>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <div className={classes.fontText}>
                            <TextField
                                id={'cuit'}
                                name="cuit"
                                variant="filled"
                                label="CUIT"
                                value={cuit}
                                onChange={onChangeCuitNumber}
                                error={!!errorMsg}
                                disabled={alreadyRegisteredUser || showRegisterLink}
                                helperText={errorMsg || helperText || "El que usás para facturar sin espacios ni guiones."}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position="end">
                                            {okCheck ? <CheckBoxIcon className={classes.tickStyle} /> : ""}
                                        </InputAdornment>
                                }}
                                autoFocus
                                fullWidth
                            />
                        </div>
                    </Grid>
                    <GoogleReCaptcha
                        action={dynamicAction}
                        onVerify={setToken}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={!isValid || loading}
                        />
                    </GoogleReCaptcha>

                    {
                        alreadyRegisteredUser ?
                            <Grid container className={classes.tableStyle} justify="center" id={'confirmed-account-msg'}>
                                <Grid item xs={12} sm={12}>
                                    <Box display="flex" flexDirection="row" pl={5} pt={2}>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td><CheckCircleOutlineIcon className={classes.image} /></td>
                                                    <td><p className={classes.textStyle}>!Tu comercio <strong>ya fue dado
                                                        de alta</strong> en Cuenta Payway para ventas online!<br />Podés comenzar a operar ingresando
                                                        a <br/>
                                                        <Link href={URL_BACKOFFICE} onClick={goToBackoffice}
                                                              className={classes.linkStyle2}><u>{URL_BACKOFFICE}</u></Link>
                                                    </p>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Box display="flex" flexDirection="row" pl={5} pb={2}>
                                        <Box m={1}>
                                            <Button id={'reset-cuit-btn'} className={classes.buttonStyle} variant="contained"
                                                    onClick={resetForm}>
                                                <ArrowBackIosIcon/> Verificar otro CUIT
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid> : null
                    }

                    <Grid item xs={12} className={classes.buttonAction}>
                        {
                            !showConfirmEmail && !showConfirmEmailSent && !alreadyRegisteredUser ?
                                showRegisterLink ?
                                    <ValidationMessages
                                        image={classes.image}
                                        subtitle={classes.subtitle}
                                        linkStyle={classes.linkStyle}
                                        buttonStyle={classes.buttonStyle}
                                        text={
                                            <h3 className={classes.subtitle}>¿Tu comercio aún
                                                no {showRegisterText ? 'está adherido?' : 'cuenta con un establecimiento e-commerce?'}<br/>
                                                <Link href={URL_PRISMA_HOME}  onClick={goToPrismaRegister}
                                                      className={classes.linkStyle}><u>{showRegisterText ? 'Hacelo' : 'Solicitalo'} ahora</u></Link> para crear tu cuenta Payway para ventas online.
                                            </h3>
                                        }
                                        resetForm={resetForm}
                                    > </ValidationMessages>
                                    : showSEMLink ?
                                        <ValidationMessages
                                            image={classes.image}
                                            subtitle={classes.subtitle}
                                            linkStyle={classes.linkStyle}
                                            buttonStyle={classes.buttonStyle}
                                            text={
                                                <h3 className={classes.subtitle}> Para continuar es necesario darse de alta como cliente Payway y tener asociado un establecimiento e-commerce.
                                                    <br/> Si aún no lo hiciste, visitá el sitio de  <Link href="#" onClick={goToPrismaRegister} className={classes.linkStyle}><u> Payway</u></Link>.
                                                </h3>
                                            }
                                            resetForm={resetForm}
                                        > </ValidationMessages>
                                        :  <Button
                                            id={'cuit-submit-button'}
                                            className={classes.buttonContinueStyle}
                                            variant="contained"
                                            type="submit"
                                            disabled={!isValid || loading}
                                        >
                                          <Box display="flex" alignItems="center">
                                              CONTINUAR
                                              {loading && (
                                                 <CircularProgress
                                                    size={24}
                                                    className={classes.circularProgress}
                                                 />
                                              )}
                                          </Box>
                                        </Button>
                                :  null
                        }
                    </Grid>
                </Grid>
            </form>
        </React.Fragment>
    );
}

export default CuitForm;
